import { sanitizeTextLayerFontFamily } from './textLayerFonts';

const GOOGLE_FONTS_BASE_URL = 'https://fonts.googleapis.com/css2';
const REQUESTED_WEIGHTS = [400, 600, 700];
const pendingFamilyLoads = new Map();
const loadedFamilies = new Set();
const failedFamilies = new Set();

const FONT_LINK_TIMEOUT_MS = 4000;
const FONT_READY_TIMEOUT_MS = 4000;

const sleep = (ms) => new Promise((resolve) => {
  setTimeout(resolve, Math.max(0, ms || 0));
});

const withTimeout = async (promise, timeoutMs) => (
  Promise.race([
    Promise.resolve(promise),
    sleep(timeoutMs),
  ])
);

const encodeFamilyForQuery = (family) => (
  encodeURIComponent(String(family).trim()).replace(/%20/g, '+')
);

const getFontLinkId = (family) => (
  `google-font-${String(family).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
);

const buildGoogleFontHref = (family) => {
  const encodedFamily = encodeFamilyForQuery(family);
  const weightQuery = REQUESTED_WEIGHTS.join(';');
  return `${GOOGLE_FONTS_BASE_URL}?family=${encodedFamily}:wght@${weightQuery}&display=swap`;
};

const ensureFontLinkElement = (family) => {
  if (typeof document === 'undefined') return null;
  const head = document.head || document.querySelector('head');
  if (!head) return null;

  const linkId = getFontLinkId(family);
  const existing = document.getElementById(linkId);
  if (existing && existing.tagName?.toLowerCase() === 'link') {
    return existing;
  }

  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = buildGoogleFontHref(family);
  link.setAttribute('data-font-family', family);
  head.appendChild(link);
  return link;
};

const waitForStylesheetLoad = async (link) => {
  if (!link) return;
  if (typeof document === 'undefined') return;

  await withTimeout(new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      link.removeEventListener('load', handleLoad);
      link.removeEventListener('error', handleError);
      resolve();
    };
    const handleLoad = () => finish();
    const handleError = () => finish();

    // Existing links may already be loaded; continue immediately when sheet exists.
    if (link.sheet) {
      resolve();
      return;
    }

    link.addEventListener('load', handleLoad, { once: true });
    link.addEventListener('error', handleError, { once: true });
  }), FONT_LINK_TIMEOUT_MS);
};

const waitForDocumentFonts = async (family) => {
  if (typeof document === 'undefined') return;
  const fontSet = document.fonts;
  if (!fontSet || typeof fontSet.load !== 'function') return;
  const requests = REQUESTED_WEIGHTS.map((weight) => (
    fontSet.load(`${weight} 16px "${family}"`, 'AaBbYyZz 0123456789')
  ));
  await withTimeout(Promise.all(requests), FONT_READY_TIMEOUT_MS);
};

export const ensureGoogleFontLoaded = async (family) => {
  const sanitizedFamily = sanitizeTextLayerFontFamily(family);
  if (!sanitizedFamily) return false;
  if (loadedFamilies.has(sanitizedFamily)) return true;

  const existingPending = pendingFamilyLoads.get(sanitizedFamily);
  if (existingPending) return existingPending;

  const pending = (async () => {
    try {
      const canAwaitDocumentFonts = Boolean(
        typeof document !== 'undefined'
        && document.fonts
        && typeof document.fonts.load === 'function',
      );
      if (!canAwaitDocumentFonts) {
        ensureFontLinkElement(sanitizedFamily);
        loadedFamilies.add(sanitizedFamily);
        failedFamilies.delete(sanitizedFamily);
        return true;
      }

      const link = ensureFontLinkElement(sanitizedFamily);
      if (link) await waitForStylesheetLoad(link);
      await waitForDocumentFonts(sanitizedFamily);
      loadedFamilies.add(sanitizedFamily);
      failedFamilies.delete(sanitizedFamily);
      return true;
    } catch (error) {
      failedFamilies.add(sanitizedFamily);
      console.warn('[LowerThirds] Failed to load Google font', sanitizedFamily, error);
      return false;
    } finally {
      pendingFamilyLoads.delete(sanitizedFamily);
    }
  })();

  pendingFamilyLoads.set(sanitizedFamily, pending);
  return pending;
};

export const ensureGoogleFontsLoaded = async (families) => {
  const source = Array.isArray(families) ? families : [];
  if (!source.length) return [];
  return Promise.all(source.map((family) => ensureGoogleFontLoaded(family)));
};

// Optional test helper if future tests need to reset module state.
export const __resetGoogleFontLoaderStateForTests = () => {
  pendingFamilyLoads.clear();
  loadedFamilies.clear();
  failedFamilies.clear();
};
